/**
 * archive.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";

import { StoredFile } from "../models/file";
import { ArchiveEntry } from "../models/message";

import { http } from "./http.service";

export interface ArchiveService {
  checkUnlocked(accountID: string): Promise<[DateTime, DateTime] | []>,
  lockArchive(accountID: string): Promise<void>,
  unlockArchive(accountID: string, startDate: DateTime, endDate: DateTime, pemPrivateKey: string): Promise<void>,
  searchArchive(accountID: string, startDate: DateTime, endDate: DateTime, terms?: SearchTerms): Promise<Array<ArchiveEntry>>,
  exportLink(accountID: string, token: string, startDate: DateTime, endDate: DateTime, query?: SearchTerms): string,
  searchFiles(accountID: string, terms: FileSearchTerms): Promise<Array<StoredFile>>,
  getDownloadKey(accountID: string, groupID: string, fileID: string, keyID: string, pemPrivateKey: string): Promise<string>,
  makeFileDownloadLink (accountID: string, groupID: string, fileID: string, decryptKey: string, token: string): Promise<string>,
  kyberPemFormat(input: string): string
}

interface UnlockRequestPayload {
  start: number,
  end: number,
  key: string
}

export interface SearchTerms {
  groupIDs?: Array<string>,
  userIDs?: Array<string>,
  deviceIDs?: Array<string>,
  terms?: Array<string>
}
interface SearchRequestPayload extends SearchTerms {
  start: number,
  end: number
}

export interface ArchiveEntryJSON {
  _id: string,
  msgtype: string,
  accountID: string,
  userID: string,
  deviceID?: string,
  groupID: string,
  threadID?: string,
  msgID?: string,
  created: string | DateTime,
  sent: string | DateTime,
  content?: string,
  contenttype?: string,
  firstname?: string,
  lastname?: string,
  groupname?: string,
  deviceName?: string
}

export interface FileSearchTerms {
  startDate?: number,
  endDate?: number,
  userIDs?: Array<string>,
  fileIDs?: Array<string>,
  terms?: Array<string>
}

class ArchiveServiceImpl implements ArchiveService {
  async checkUnlocked(accountID: string): Promise<[DateTime, DateTime] | []> {
    const values = await http.get<[string, string] | []>(`/account/${encodeURIComponent(accountID)}/archive/check/`);
    if (!values[0] || !values[1]) { return []; }
    return [DateTime.fromISO(values[0]), DateTime.fromISO(values[1])];
  }
  async unlockArchive(accountID: string, startDate: DateTime, endDate: DateTime, pemPrivateKey: string): Promise<void> {
    const key = this.pemToBase64(pemPrivateKey);

    await http.post<UnlockRequestPayload, void>(`/account/${encodeURIComponent(accountID)}/archive/unlock/`, {
      start: startDate.toMillis(),
      end: endDate.toMillis(),
      key: key
    });
  }

  lockArchive(accountID: string): Promise<void> {
    return http.post<Record<string, never>, void>(
      `/account/${encodeURIComponent(accountID)}/archive/lock/`, {});
  }

  async searchArchive(accountID: string, startDate: DateTime, endDate: DateTime, terms?: SearchTerms): Promise<Array<ArchiveEntry>> {
    const results = await http.post<SearchRequestPayload, Array<ArchiveEntryJSON>>(`/account/${encodeURIComponent(accountID)}/archive/search/`, {
      start: startDate.toMillis(),
      end: endDate.toMillis(),
      ...terms
    });
    for (const e of results) {
      e.created = DateTime.fromISO(e.created as string);
      e.sent = DateTime.fromISO(e.sent as string);
    }
    return results as Array<ArchiveEntry>;
  }

  exportLink(accountID: string, token: string, startDate: DateTime, endDate: DateTime, query?: SearchTerms): string {
    const params = new URLSearchParams();
    params.set("start", startDate.toMillis().toString());
    params.set("end", endDate.toMillis().toString());
    if (query) {
      for (const key in query) {
        if (!Object.prototype.hasOwnProperty.call(query, key)) { continue; }
        const val = query[key as keyof SearchTerms];
        if (!val) { continue; }
        for (const v of val) {
          params.append(key, v);
        }
      }
    }

    accountID = encodeURIComponent(accountID);
    return `/account/${accountID}/archive/export?${params.toString()}&token=${token}`;
  }

  async searchFiles(accountID: string, terms: FileSearchTerms): Promise<Array<StoredFile>> {
    const results = await http.post<FileSearchTerms, Array<StoredFile>>(
      `/account/${encodeURIComponent(accountID)}/archive/filesearch/`, terms);
    for (const e of results) {
      if (e.created) {
        e.created = DateTime.fromISO(e.created as unknown as string);
      }
    }
    return results;
  }

  async getDownloadKey(accountID: string, groupID: string, fileID: string,
      keyID: string, pemPrivateKey: string): Promise<string> {
    const key = this.pemToBase64(pemPrivateKey);
    const res = await http.post<any, {decryptKey: string}>(
      `/account/${encodeURIComponent(accountID)}/archive/filekey`, {
        groupID: groupID,
        fileID: fileID,
        keyID: keyID,
        kyberPrivateKey: key
      });
    return res.decryptKey;
  }

  async makeFileDownloadLink (accountID: string, groupID: string, fileID: string,
      decryptKey: string, token: string): Promise<string> {
    accountID = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    fileID = encodeURIComponent(fileID);
    const key = encodeURIComponent(decryptKey);
    const path = `/account/${accountID}/group/${groupID}/file/${fileID}`;
    const fileHeaders = await http.head(path);
    console.log("file info:", fileHeaders);
    return `${path}?key=${key}&token=${token}`;
  }

  kyberPemFormat(input: string): string {
    return `-----BEGIN SC KYBER PRIVATE KEY-----\n${input.match(/.{1,64}/g)?.join("\n") || ""}\n-----END SC KYBER PRIVATE KEY-----\n`;
  }
  pemToBase64(input: string): string {
    input = input.trim();
    if (!input.startsWith("-----BEGIN SC KYBER PRIVATE KEY-----") ||
        !input.endsWith("-----END SC KYBER PRIVATE KEY-----")) {
      throw new Error("Missing PEM formatting markers");
    }
    const parts = input
      .replace("-----BEGIN SC KYBER PRIVATE KEY-----", "")
      .replace("-----END SC KYBER PRIVATE KEY-----", "").split("\n");
    return parts.join("");
  }
}

let archiveService: ArchiveServiceImpl;

export function archiveServiceFactory(): ArchiveService {
  if (!archiveService) { archiveService = new ArchiveServiceImpl(); }
  return archiveService;
}